/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { IMenuItem } from "@atg/icf-react-components/components/SideMenu";
import { TFunction } from "i18next";

import { AuthenticatedMenuItem, GuestMenuItem } from "~/features/header/constants";

interface AuthenticatedArgs {
  viewOnly: boolean;
  hasOneClick: boolean;
  t: TFunction;
}

interface GuestArgs {
  t: TFunction;
}

interface AuthenticatedArgsMobile extends AuthenticatedArgs {
  handleNavigate: (link: string) => void;
}

interface GuestArgsMobile extends GuestArgs {
  handleNavigate: (link: string) => void;
}

export interface MenuItems {
  [key: string]: {
    key: string;
    label: string;
    to: string;
    disabled: boolean;
    available: boolean;
  };
}

export const getGuestMenuItems: ({ t }: GuestArgs) => MenuItems = ({ t }) => ({
  [GuestMenuItem.ABOUT]: {
    key: GuestMenuItem.ABOUT,
    label: t("menu.about"),
    to: "/#about",
    disabled: false,
    available: true,
  },
  [GuestMenuItem.PARTNERS]: {
    key: GuestMenuItem.PARTNERS,
    label: t("menu.partners"),
    to: "/#partners",
    disabled: false,
    available: true,
  },
  [GuestMenuItem.PROGRAMS]: {
    key: GuestMenuItem.PROGRAMS,
    label: t("menu.programs"),
    to: "/#programs",
    disabled: false,
    available: true,
  },
  [GuestMenuItem.TOOLS]: {
    key: GuestMenuItem.TOOLS,
    label: t("menu.tools"),
    to: "/#tools",
    disabled: false,
    available: true,
  },
  [GuestMenuItem.CONTACT]: {
    key: GuestMenuItem.CONTACT,
    label: t("menu.contact"),
    to: "/#contact",
    disabled: false,
    available: true,
  },
  [GuestMenuItem.SIGNUP]: {
    key: GuestMenuItem.SIGNUP,
    label: t("menu.sign_up"),
    to: "/app/signup",
    disabled: false,
    available: true,
  },
  [GuestMenuItem.LOGIN]: {
    key: GuestMenuItem.LOGIN,
    label: t("menu.login"),
    to: "/app/login",
    disabled: false,
    available: true,
  },
});

export const useAuthenticatedMenuItems: ({ viewOnly, hasOneClick, t }: AuthenticatedArgs) => MenuItems = ({
  viewOnly,
  hasOneClick,
  t,
}) => {
  return {
    [AuthenticatedMenuItem.DASHBOARD]: {
      key: AuthenticatedMenuItem.DASHBOARD,
      label: t("menu.dashboard"),
      to: "/app/dashboard",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.STATISTICS]: {
      key: AuthenticatedMenuItem.STATISTICS,
      label: t("menu.statistics"),
      to: "/app/statistics",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.PROMO_TOOLS]: {
      key: AuthenticatedMenuItem.PROMO_TOOLS,
      label: t("menu.promo_tools"),
      to: "/app/promo-tools",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.MY_SITES]: {
      key: AuthenticatedMenuItem.MY_SITES,
      label: t("menu.my_sites"),
      to: "",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.MANAGE_SITES]: {
      key: AuthenticatedMenuItem.MANAGE_SITES,
      label: t("menu.manage-sites"),
      to: "/app/my-sites/manage-sites",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.CREATE_NEW_SITE]: {
      key: AuthenticatedMenuItem.CREATE_NEW_SITE,
      label: t("menu.create-new-site"),
      to: "/app/my-sites/create-new-site",
      disabled: viewOnly,
      available: true,
    },
    [AuthenticatedMenuItem.POSTBACKS]: {
      key: AuthenticatedMenuItem.POSTBACKS,
      label: t("menu.postbacks"),
      to: "/app/my-sites/postbacks",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.SUPPORT]: {
      key: AuthenticatedMenuItem.SUPPORT,
      label: t("menu.support"),
      to: "",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.FAQ]: {
      key: AuthenticatedMenuItem.FAQ,
      label: t("menu.faq"),
      to: "/app/support/faq",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.API]: {
      key: AuthenticatedMenuItem.API,
      label: t("menu.api"),
      to: "/app/support/api",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.CONTACT]: {
      key: AuthenticatedMenuItem.CONTACT,
      label: t("menu.contact"),
      to: "/app/support/contact",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.ENCODE]: {
      key: AuthenticatedMenuItem.ENCODE,
      label: t("menu.encode"),
      to: "/app/one-click/encoding",
      disabled: false,
      available: hasOneClick,
    },
    [AuthenticatedMenuItem.DECODE]: {
      key: AuthenticatedMenuItem.DECODE,
      label: t("menu.decode"),
      to: "/app/one-click/decoding",
      disabled: false,
      available: hasOneClick,
    },
    [AuthenticatedMenuItem.MY_ACCOUNT]: {
      key: AuthenticatedMenuItem.MY_ACCOUNT,
      label: t("menu.my_account"),
      to: "",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.MY_DETAILS]: {
      key: "my-details",
      label: t("menu.my_details"),
      to: "/app/my-account/my-details",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.PAYOUT_INFO]: {
      key: AuthenticatedMenuItem.PAYOUT_INFO,
      label: t("menu.payout_method"),
      to: "/app/my-account/documents#payout-information",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.DOCUMENTS]: {
      key: AuthenticatedMenuItem.DOCUMENTS,
      label: t("menu.compliance"),
      to: "/app/my-account/documents#document-upload",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.PERSONAL_INFORMATION]: {
      key: AuthenticatedMenuItem.PERSONAL_INFORMATION,
      label: t("menu.payee_information"),
      to: "/app/my-account/documents#personal-information",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.EMAIL_PREFERENCES]: {
      key: AuthenticatedMenuItem.EMAIL_PREFERENCES,
      label: t("menu.email_preferences"),
      to: "/app/my-account/email-preferences",
      disabled: false,
      available: true,
    },
    [AuthenticatedMenuItem.LOGOUT]: {
      key: AuthenticatedMenuItem.LOGOUT,
      label: t("menu.logout"),
      to: "/app/logout",
      disabled: false,
      available: true,
    },
  };
};

export const getGuestMobileMenuItems: ({ t, handleNavigate }: GuestArgsMobile) => IMenuItem[] = ({
  t,
  handleNavigate,
}) => {
  const menuItems = getGuestMenuItems({ t });

  return [
    ...(menuItems[GuestMenuItem.ABOUT].available
      ? [
          {
            key: menuItems[GuestMenuItem.ABOUT].key,
            label: menuItems[GuestMenuItem.ABOUT].label,
            onClick: () => handleNavigate(menuItems[GuestMenuItem.ABOUT].to),
            isDisabled: menuItems[GuestMenuItem.ABOUT].disabled,
          },
        ]
      : []),
    ...(menuItems[GuestMenuItem.PARTNERS].available
      ? [
          {
            key: menuItems[GuestMenuItem.PARTNERS].key,
            label: menuItems[GuestMenuItem.PARTNERS].label,
            onClick: () => handleNavigate(menuItems[GuestMenuItem.PARTNERS].to),
            isDisabled: menuItems[GuestMenuItem.PARTNERS].disabled,
          },
        ]
      : []),
    ...(menuItems[GuestMenuItem.PROGRAMS].available
      ? [
          {
            key: menuItems[GuestMenuItem.PROGRAMS].key,
            label: menuItems[GuestMenuItem.PROGRAMS].label,
            onClick: () => handleNavigate(menuItems[GuestMenuItem.PROGRAMS].to),
            isDisabled: menuItems[GuestMenuItem.PROGRAMS].disabled,
          },
        ]
      : []),
    ...(menuItems[GuestMenuItem.TOOLS].available
      ? [
          {
            key: menuItems[GuestMenuItem.TOOLS].key,
            label: menuItems[GuestMenuItem.TOOLS].label,
            onClick: () => handleNavigate(menuItems[GuestMenuItem.TOOLS].to),
            isDisabled: menuItems[GuestMenuItem.TOOLS].disabled,
          },
        ]
      : []),
    ...(menuItems[GuestMenuItem.CONTACT].available
      ? [
          {
            key: menuItems[GuestMenuItem.CONTACT].key,
            label: menuItems[GuestMenuItem.CONTACT].label,
            onClick: () => handleNavigate(menuItems[GuestMenuItem.CONTACT].to),
            isDisabled: menuItems[GuestMenuItem.CONTACT].disabled,
          },
        ]
      : []),
    ...(menuItems[GuestMenuItem.SIGNUP].available
      ? [
          {
            key: menuItems[GuestMenuItem.SIGNUP].key,
            label: menuItems[GuestMenuItem.SIGNUP].label,
            onClick: () => handleNavigate(menuItems[GuestMenuItem.SIGNUP].to),
            isDisabled: menuItems[GuestMenuItem.SIGNUP].disabled,
          },
        ]
      : []),
    ...(menuItems[GuestMenuItem.LOGIN].available
      ? [
          {
            key: menuItems[GuestMenuItem.LOGIN].key,
            label: menuItems[GuestMenuItem.LOGIN].label,
            onClick: () => handleNavigate(menuItems[GuestMenuItem.LOGIN].to),
            isDisabled: menuItems[GuestMenuItem.LOGIN].disabled,
          },
        ]
      : []),
  ];
};

export const useAuthenticatedMobileMenuItems: ({
  viewOnly,
  hasOneClick,
  t,
  handleNavigate,
}: AuthenticatedArgsMobile) => IMenuItem[] = ({ viewOnly, hasOneClick, t, handleNavigate }) => {
  const menuItems = useAuthenticatedMenuItems({ viewOnly, hasOneClick, t });

  return [
    ...(menuItems[AuthenticatedMenuItem.DASHBOARD].available
      ? [
          {
            key: menuItems[AuthenticatedMenuItem.DASHBOARD].key,
            label: menuItems[AuthenticatedMenuItem.DASHBOARD].label,
            onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.DASHBOARD].to),
            isDisabled: menuItems[AuthenticatedMenuItem.DASHBOARD].disabled,
          },
        ]
      : []),
    ...(menuItems[AuthenticatedMenuItem.STATISTICS].available
      ? [
          {
            key: menuItems[AuthenticatedMenuItem.STATISTICS].key,
            label: menuItems[AuthenticatedMenuItem.STATISTICS].label,
            onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.STATISTICS].to),
            isDisabled: menuItems[AuthenticatedMenuItem.STATISTICS].disabled,
          },
        ]
      : []),
    ...(menuItems[AuthenticatedMenuItem.PROMO_TOOLS].available
      ? [
          {
            key: menuItems[AuthenticatedMenuItem.PROMO_TOOLS].key,
            label: menuItems[AuthenticatedMenuItem.PROMO_TOOLS].label,
            onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.PROMO_TOOLS].to),
            isDisabled: menuItems[AuthenticatedMenuItem.PROMO_TOOLS].disabled,
          },
        ]
      : []),
    ...(menuItems[AuthenticatedMenuItem.MY_SITES].available
      ? [
          {
            key: menuItems[AuthenticatedMenuItem.MY_SITES].key,
            label: menuItems[AuthenticatedMenuItem.MY_SITES].label,
            isDisabled: menuItems[AuthenticatedMenuItem.PROMO_TOOLS].disabled,
            submenus: [
              ...(menuItems[AuthenticatedMenuItem.MANAGE_SITES].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.MANAGE_SITES].key,
                      label: menuItems[AuthenticatedMenuItem.MANAGE_SITES].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.MANAGE_SITES].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.MANAGE_SITES].disabled,
                    },
                  ]
                : []),
              ...(menuItems[AuthenticatedMenuItem.MANAGE_SITES].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.CREATE_NEW_SITE].key,
                      label: menuItems[AuthenticatedMenuItem.CREATE_NEW_SITE].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.CREATE_NEW_SITE].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.CREATE_NEW_SITE].disabled,
                    },
                  ]
                : []),
              ...(menuItems[AuthenticatedMenuItem.POSTBACKS].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.POSTBACKS].key,
                      label: menuItems[AuthenticatedMenuItem.POSTBACKS].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.POSTBACKS].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.POSTBACKS].disabled,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(menuItems[AuthenticatedMenuItem.SUPPORT].available
      ? [
          {
            key: menuItems[AuthenticatedMenuItem.SUPPORT].key,
            label: menuItems[AuthenticatedMenuItem.SUPPORT].label,
            isDisabled: menuItems[AuthenticatedMenuItem.SUPPORT].disabled,
            submenus: [
              ...(menuItems[AuthenticatedMenuItem.FAQ].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.FAQ].key,
                      label: menuItems[AuthenticatedMenuItem.FAQ].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.FAQ].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.FAQ].disabled,
                    },
                  ]
                : []),
              ...(menuItems[AuthenticatedMenuItem.API].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.API].key,
                      label: menuItems[AuthenticatedMenuItem.API].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.API].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.API].disabled,
                    },
                  ]
                : []),
              ...(menuItems[AuthenticatedMenuItem.CONTACT].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.CONTACT].key,
                      label: menuItems[AuthenticatedMenuItem.CONTACT].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.CONTACT].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.CONTACT].disabled,
                    },
                  ]
                : []),
              ...(menuItems[AuthenticatedMenuItem.ENCODE].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.ENCODE].key,
                      label: menuItems[AuthenticatedMenuItem.ENCODE].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.ENCODE].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.ENCODE].disabled,
                    },
                  ]
                : []),
              ...(menuItems[AuthenticatedMenuItem.DECODE].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.DECODE].key,
                      label: menuItems[AuthenticatedMenuItem.DECODE].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.DECODE].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.DECODE].disabled,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(menuItems[AuthenticatedMenuItem.MY_ACCOUNT].available
      ? [
          {
            key: menuItems[AuthenticatedMenuItem.MY_ACCOUNT].key,
            label: menuItems[AuthenticatedMenuItem.MY_ACCOUNT].label,
            isDisabled: menuItems[AuthenticatedMenuItem.MY_ACCOUNT].disabled,
            submenus: [
              ...(menuItems[AuthenticatedMenuItem.MY_DETAILS].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.MY_DETAILS].key,
                      label: menuItems[AuthenticatedMenuItem.MY_DETAILS].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.MY_DETAILS].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.MY_DETAILS].disabled,
                    },
                  ]
                : []),
              ...(menuItems[AuthenticatedMenuItem.PAYOUT_INFO].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.PAYOUT_INFO].key,
                      label: menuItems[AuthenticatedMenuItem.PAYOUT_INFO].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.PAYOUT_INFO].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.PAYOUT_INFO].disabled,
                    },
                  ]
                : []),
              ...(menuItems[AuthenticatedMenuItem.DOCUMENTS].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.DOCUMENTS].key,
                      label: menuItems[AuthenticatedMenuItem.DOCUMENTS].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.DOCUMENTS].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.DOCUMENTS].disabled,
                    },
                  ]
                : []),
              ...(menuItems[AuthenticatedMenuItem.EMAIL_PREFERENCES].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.EMAIL_PREFERENCES].key,
                      label: menuItems[AuthenticatedMenuItem.EMAIL_PREFERENCES].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.EMAIL_PREFERENCES].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.EMAIL_PREFERENCES].disabled,
                    },
                  ]
                : []),
              ...(menuItems[AuthenticatedMenuItem.LOGOUT].available
                ? [
                    {
                      key: menuItems[AuthenticatedMenuItem.LOGOUT].key,
                      label: menuItems[AuthenticatedMenuItem.LOGOUT].label,
                      onClick: () => handleNavigate(menuItems[AuthenticatedMenuItem.LOGOUT].to),
                      isDisabled: menuItems[AuthenticatedMenuItem.LOGOUT].disabled,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];
};
